import { unwrap, type SetupDraftIn, type WorkspaceOut } from "@sagot/api-client";
import { canonicalIndustry } from "@sagot/constants";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { queryKeys } from "../lib/query-keys";
import { useApi } from "../session/session-provider";
import { readOnboarding } from "./use-onboarding";

type Goal = NonNullable<SetupDraftIn["goals"]>[number];

/** Draft request from the workspace profile + step-1 answers (nothing is saved by drafting). */
export function draftRequest(workspace: WorkspaceOut, mode: "ai" | "template", description?: string): SetupDraftIn {
  const data = readOnboarding(workspace).data;
  const text = (description ?? data.description ?? "").trim();
  return {
    industry: canonicalIndustry(workspace.industry) ?? undefined,
    industry_label: workspace.industry_label ?? undefined,
    goals: workspace.goals as Goal[],
    profile: workspace.profile as SetupDraftIn["profile"],
    description: text || undefined,
    template_key: (data.template_key ?? undefined) as SetupDraftIn["template_key"],
    mode,
  };
}

/** First draft for step 3 (AI when the user asked for it in step 1, else the template). */
export function useSetupDraft(workspace: WorkspaceOut | undefined, enabled: boolean) {
  const api = useApi();
  const data = readOnboarding(workspace).data;
  const mode = data.generate && data.description?.trim() ? "ai" : "template";
  return useQuery({
    queryKey: ["setup-draft", workspace?.id ?? "none", mode],
    queryFn: () => {
      if (!workspace) throw new Error("workspace required");
      return unwrap(api.POST("/v1/workspace/setup-draft", { body: draftRequest(workspace, mode) }));
    },
    enabled: enabled && Boolean(workspace),
    staleTime: Infinity,
    retry: false,
  });
}

/** "Generate with AI" from the configure step (optionally with an edited description). */
export function useRegenerateDraft(workspace: WorkspaceOut | undefined) {
  const api = useApi();
  return useMutation({
    mutationFn: (description: string) => {
      if (!workspace) throw new Error("workspace required");
      return unwrap(api.POST("/v1/workspace/setup-draft", { body: draftRequest(workspace, "ai", description) }));
    },
  });
}

/**
 * Fresh deterministic draft of one template (the step-3 "Purpose template" switcher). Template
 * mode makes no LLM call and has its own generous per-workspace limit (AI drafts stay strict).
 * Cached per template and setup (industry, goals, name, language), so going back to a template
 * is instant.
 */
export function useTemplateDraft(workspace: WorkspaceOut | undefined) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useCallback(
    (templateKey: string) => {
      if (!workspace) return Promise.reject(new Error("workspace required"));
      const body: SetupDraftIn = { ...draftRequest(workspace, "template"), template_key: templateKey as SetupDraftIn["template_key"] };
      const setup = JSON.stringify([body.industry, body.goals, workspace.name, workspace.default_language]);
      return queryClient.query({
        queryKey: queryKeys.templateDraft(workspace.id, templateKey, setup),
        queryFn: () => unwrap(api.POST("/v1/workspace/setup-draft", { body })),
        staleTime: Infinity,
      });
    },
    [api, queryClient, workspace],
  );
}
